import { useAuth } from '@core/context/AuthContext';
import { useInventoryState } from '@core/context/InventoryContext';
import { 
    collection, 
    onSnapshot, 
    query, 
    orderBy, 
    addDoc, 
    deleteDoc, 
    doc, 
    updateDoc,
    serverTimestamp,
    db 
} from '@core/firebase';
import { useState, useEffect, useCallback } from 'react';

/**
 * useCoordinates - Hook to manage Aisle and Rack catalogs.
 */
export const useCoordinates = () => {
    const [aisles, setAisles] = useState([]);
    const [racks, setRacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user } = useAuth();
    const { products } = useInventoryState();

    useEffect(() => {
        let aislesLoaded = false;
        let racksLoaded = false;

        // Aisle Subscription
        const qAisles = query(collection(db, 'aisles'), orderBy('name', 'asc'));
        const unsubAisles = onSnapshot(qAisles, (snap) => {
            setAisles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            aislesLoaded = true;
            if (racksLoaded) setLoading(false);
        }, (err) => setError(err.message));

        // Rack Subscription
        const qRacks = query(collection(db, 'racks'), orderBy('name', 'asc'));
        const unsubRacks = onSnapshot(qRacks, (snap) => {
            setRacks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            racksLoaded = true;
            if (aislesLoaded) setLoading(false);
        }, (err) => setError(err.message));

        return () => {
            unsubAisles();
            unsubRacks();
        };
    }, []);

    const addAisle = useCallback(async (name, locationId, description = '') => {
        if (!name?.trim()) throw new Error("Aisle name required");
        if (!locationId) throw new Error("You must select a Zona");
        const normalizedName = name.trim();
        const docRef = await addDoc(collection(db, 'aisles'), {
            name: normalizedName,
            description: description.trim(),
            locationId,
            isActive: true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_AISLE',
            product_name: normalizedName,
            product_description: `New Shelf/Aisle: ${normalizedName}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Shelf ${normalizedName} created`
        });

        return docRef.id;
    }, [user]);

    const updateAisle = useCallback(async (id, newName, locationId, newDescription = '') => {
        if (!newName?.trim()) throw new Error("Name required");
        const normalizedName = newName.trim();
        await updateDoc(doc(db, 'aisles', id), {
            name: normalizedName,
            description: newDescription.trim(),
            locationId,
            updatedAt: serverTimestamp()
        });

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_AISLE',
            product_name: normalizedName,
            product_description: `Updated Shelf/Aisle: ${normalizedName}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Shelf metadata updated`
        });
    }, [user]);

    const deleteAisle = useCallback(async (id, options = {}) => {
        const target = aisles.find(a => a.id === id);
        if (!target) return;

        const { force = false } = options;
        if (!force) {
            const hasItems = products.some(p => p.location?.aisle === target.name);
            if (hasItems) {
                throw new Error(`Cannot delete "${target.name}": This shelf contains active items.`);
            }
        }

        await deleteDoc(doc(db, 'aisles', id));

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_AISLE',
            product_name: target.name,
            product_description: `Deleted Shelf/Aisle: ${target.name}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Shelf removed from system`
        });
    }, [aisles, products, user]);

    const addRack = useCallback(async (name, aisleId, description = '') => {
        if (!name?.trim()) throw new Error("Rack name required");
        if (!aisleId) throw new Error("You must select an Aisle");
        const normalizedName = name.trim();
        const docRef = await addDoc(collection(db, 'racks'), {
            name: normalizedName,
            description: description.trim(),
            aisleId,
            isActive: true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_RACK',
            product_name: normalizedName,
            product_description: `New Floor/Rack: ${normalizedName}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Floor/Rack ${normalizedName} created`
        });

        return docRef.id;
    }, [user]);

    const updateRack = useCallback(async (id, newName, aisleId, newDescription = '') => {
        if (!newName?.trim()) throw new Error("Name required");
        const normalizedName = newName.trim();
        await updateDoc(doc(db, 'racks', id), {
            name: normalizedName,
            description: newDescription.trim(),
            aisleId,
            updatedAt: serverTimestamp()
        });

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_RACK',
            product_name: normalizedName,
            product_description: `Updated Floor/Rack: ${normalizedName}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Floor/Rack metadata updated`
        });
    }, [user]);

    const deleteRack = useCallback(async (id, options = {}) => {
        const target = racks.find(r => r.id === id);
        if (!target) return;

        const { force = false } = options;
        if (!force) {
            const hasItems = products.some(p => p.location?.rack === target.name);
            if (hasItems) {
                throw new Error(`Cannot delete "${target.name}": This floor contains active items.`);
            }
        }

        await deleteDoc(doc(db, 'racks', id));

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_RACK',
            product_name: target.name,
            product_description: `Deleted Floor/Rack: ${target.name}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Floor/Rack removed from system`
        });
    }, [racks, products, user]);

    const toggleAisleStatus = useCallback(async (id, currentStatus) => {
        const target = aisles.find(a => a.id === id);
        if (!target) return;

        const newStatus = !currentStatus;
        await updateDoc(doc(db, 'aisles', id), {
            isActive: newStatus,
            updatedAt: serverTimestamp()
        });

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_AISLE',
            product_name: target.name,
            product_description: `${newStatus ? 'Activated' : 'Deactivated'} Shelf: ${target.name}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Shelf ${newStatus ? 'activated' : 'deactivated'} in system`
        });
    }, [aisles, user]);

    const toggleRackStatus = useCallback(async (id, currentStatus) => {
        const target = racks.find(r => r.id === id);
        if (!target) return;

        const newStatus = !currentStatus;
        await updateDoc(doc(db, 'racks', id), {
            isActive: newStatus,
            updatedAt: serverTimestamp()
        });

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_RACK',
            product_name: target.name,
            product_description: `${newStatus ? 'Activated' : 'Deactivated'} Floor: ${target.name}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Floor ${newStatus ? 'activated' : 'deactivated'} in system`
        });
    }, [racks, user]);

    return { 
        aisles, 
        racks, 
        loading, 
        error, 
        addAisle, 
        updateAisle,
        deleteAisle, 
        toggleAisleStatus,
        addRack, 
        updateRack,
        deleteRack,
        toggleRackStatus 
    };
};
